import React, { useState, useRef } from "react";
import axios from "axios";
import swal from "sweetalert";

const API_KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const INITIAL = { current_password: "", new_password: "", new_confirm_password: "" };

export default function Checkout() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState({});
  const pending = useRef(false);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined, general: undefined }));
  };

  const toggle = (name) => setVisible((previous) => ({ ...previous, [name]: !previous[name] }));
  const errorText = (name) => {
    const value = errors[name];
    return Array.isArray(value) ? value.join(" ") : typeof value === "string" ? value : "";
  };

  const submit = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Authentication required", "Please sign in before changing your password.", "warning");
      return;
    }
    if (!form.current_password || !form.new_password || !form.new_confirm_password) {
      setErrors({ general: "Please complete all password fields." });
      return;
    }
    if (form.new_password !== form.new_confirm_password) {
      setErrors({ new_confirm_password: "New password and confirmation do not match." });
      return;
    }
    if (form.current_password === form.new_password) {
      setErrors({ new_password: "Choose a password different from your current password." });
      return;
    }

    pending.current = true;
    setSubmitting(true);
    setErrors({});
    try {
      const { data } = await axios.post("/apps-password-change", {
        current_password: form.current_password,
        new_password: form.new_password,
        new_confirm_password: form.new_confirm_password,
        key_s: API_KEY,
      });
      if (data?.status === "success") {
        setForm(INITIAL);
        setVisible({});
        swal("Success", data.message || "Your password was updated.", "success");
      } else if (data?.status === "fail") {
        swal("Warning", data.message || "Password update failed.", "warning");
      } else {
        setErrors(data?.validation_error || { general: data?.message || "Unable to update password." });
      }
    } catch (error) {
      const server = error?.response?.data;
      setErrors(server?.validation_error || { general: server?.message || "A network error occurred. Please try again." });
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  const fields = [
    { name: "current_password", label: "Current Password", hint: "Enter your existing account password", autoComplete: "current-password" },
    { name: "new_password", label: "New Password", hint: "Create a new, unique password", autoComplete: "new-password" },
    { name: "new_confirm_password", label: "Confirm New Password", hint: "Enter your new password again", autoComplete: "new-password" },
  ];

  return (
    <>
      <style>{`
        html, body, #root { background: #00191D !important; }
        .rm-pw, .rm-pw * { box-sizing: border-box; }
        .rm-pw { --navy:#00191D; --cyan:#16B8C7; --white:#fff; --muted:#a8c1c5; min-height:100vh; width:100%; background:#00191D; color:#fff; padding:22px 4px 42px; font-family:inherit; font-size:14px; line-height:1.55; }
        .rm-pw-container { max-width:1080px; margin:auto; }
        .rm-pw-heading { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:18px; padding:0 4px; }
        .rm-pw-kicker { color:#60dce6; font-size:12px; font-weight:700; letter-spacing:1.4px; text-transform:uppercase; }
        .rm-pw-heading h1 { color:#fff; margin:3px 0 0; font-size:clamp(22px,3vw,30px); line-height:1.25; font-weight:800; }
        .rm-pw-heading p { margin:7px 0 0; color:var(--muted); }
        .rm-pw-tag { display:inline-flex; align-items:center; gap:8px; flex-shrink:0; border:1px solid #22545b; background:#082b32; border-radius:30px; padding:8px 13px; color:#b8f7f8; font-size:12px; }
        .rm-pw-dot { height:8px; width:8px; border-radius:50%; background:#16B8C7; }
        .rm-pw-layout { display:grid; grid-template-columns:minmax(0,1.45fr) minmax(260px,.85fr); gap:16px; align-items:start; }
        .rm-pw-panel { border-radius:22px; border:1px solid #22505a; background:linear-gradient(155deg,#08313a,#05232b 55%,#021d24); box-shadow:0 14px 36px #0003; overflow:hidden; animation:rm-pw-enter .35s ease both; }
        .rm-pw-panel-head { padding:22px 24px 19px; border-bottom:1px solid #24464f; display:flex; gap:13px; align-items:center; }
        .rm-pw-symbol { width:46px; height:46px; flex:0 0 46px; display:grid; place-items:center; border-radius:14px; color:#00191D; background:linear-gradient(125deg,#61e7ee,#16B8C7 65%,#0c8798); }
        .rm-pw-symbol svg { width:23px; height:23px; }
        .rm-pw-panel-head h2 { font-size:18px; margin:0; font-weight:800; color:white; }
        .rm-pw-panel-head p { margin:3px 0 0; color:#a7c6cb; font-size:13px; }
        .rm-pw-form { padding:23px 24px 26px; }
        .rm-pw-field { margin-bottom:19px; }
        .rm-pw-field label { display:block; margin-bottom:8px; font-size:14px; font-weight:700; color:#e9fbfc; }
        .rm-pw-input-wrap { position:relative; }
        .rm-pw-input { display:block; width:100%; height:50px; border:1px solid #345861; border-radius:12px; outline:0; background:#00191D; color:white; padding:0 53px 0 15px; font:inherit; transition:border-color .18s,box-shadow .18s; }
        .rm-pw-input::placeholder { color:#7d9ba1; }
        .rm-pw-input:focus { border-color:#16B8C7; box-shadow:0 0 0 3px #16b8c726; }
        .rm-pw-input[aria-invalid="true"] { border-color:#ff818a; }
        .rm-pw-eye { position:absolute; right:7px; top:6px; width:39px; height:38px; display:grid; place-items:center; border:0; border-radius:9px; background:transparent; color:#9dd4db; cursor:pointer; }
        .rm-pw-eye:hover { background:#0d3941; color:#fff; }
        .rm-pw-eye svg { width:19px; height:19px; }
        .rm-pw-error { margin:6px 0 0; color:#ff9da3; font-size:12px; }
        .rm-pw-actions { border-top:1px solid #24464f; padding-top:20px; margin-top:7px; }
        .rm-pw-submit { width:100%; min-height:51px; border:0; border-radius:12px; cursor:pointer; background:linear-gradient(110deg,#4ee0e8,#16B8C7 58%,#0a94a8); color:#00191D; font-size:14px; font-weight:800; transition:transform .18s,filter .18s,box-shadow .18s; box-shadow:0 9px 23px #0db6c42c; }
        .rm-pw-submit:hover:not(:disabled) { transform:translateY(-2px); filter:brightness(1.07); box-shadow:0 12px 27px #0db6c447; }
        .rm-pw-submit:disabled { cursor:not-allowed; opacity:.65; }
        .rm-pw-side { display:grid; gap:16px; }
        .rm-pw-white { border-radius:22px; background:#fff; color:#12343c; padding:23px; border:1px solid #dcebef; box-shadow:0 12px 26px #0002; animation:rm-pw-enter .4s ease both; }
        .rm-pw-white-icon { width:42px; height:42px; border-radius:12px; display:grid; place-items:center; color:#067f8e; background:#e4f8fa; margin-bottom:15px; }
        .rm-pw-white-icon svg { width:22px; height:22px; }
        .rm-pw-white h3, .rm-pw-tip h3 { font-size:17px; font-weight:800; margin:0 0 9px; }
        .rm-pw-white p { color:#5a737a; margin:0 0 15px; font-size:14px; }
        .rm-pw-white ul { list-style:none; margin:0; padding:0; display:grid; gap:12px; }
        .rm-pw-white li { display:flex; gap:9px; align-items:flex-start; color:#24464e; font-size:13px; }
        .rm-pw-check { color:#0795a3; font-weight:900; }
        .rm-pw-tip { padding:19px 21px; background:#072b33; border:1px solid #23505a; border-radius:18px; }
        .rm-pw-tip h3 { color:#c8fbfc; }
        .rm-pw-tip p { margin:0; color:#aac6cb; font-size:13px; }
        @keyframes rm-pw-enter { from { opacity:0; transform:translateY(9px); } to { opacity:1; transform:translateY(0); } }
        @media(max-width:760px) { .rm-pw-layout { grid-template-columns:1fr; } .rm-pw-heading { align-items:flex-start; } .rm-pw-tag { display:none; } .rm-pw-panel-head { padding:18px; } .rm-pw-form { padding:18px; } .rm-pw-white { padding:19px; } }
        @media(max-width:380px) { .rm-pw-heading h1 { font-size:22px; } .rm-pw-panel-head h2 { font-size:16px; } }
        @media(prefers-reduced-motion:reduce) { .rm-pw-panel,.rm-pw-white { animation:none; } .rm-pw-submit { transition:none; } }
      `}</style>
      <main className="rm-pw">
        <div className="rm-pw-container">
          <header className="rm-pw-heading">
            <div>
              <div className="rm-pw-kicker">Account Security</div>
              <h1>Change Password</h1>
              <p>Manage your password and protect access to your account.</p>
            </div>
            <span className="rm-pw-tag"><span className="rm-pw-dot" /> Security Settings</span>
          </header>
          <div className="rm-pw-layout">
            <section className="rm-pw-panel" aria-labelledby="rm-pw-form-title">
              <div className="rm-pw-panel-head">
                <div className="rm-pw-symbol" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><path d="M12 14v3"/></svg>
                </div>
                <div><h2 id="rm-pw-form-title">Update Your Password</h2><p>Confirm your identity to save changes</p></div>
              </div>
              <form className="rm-pw-form" onSubmit={submit}>
                {fields.map((field) => (
                  <div className="rm-pw-field" key={field.name}>
                    <label htmlFor={`rm-${field.name}`}>{field.label}</label>
                    <div className="rm-pw-input-wrap">
                      <input id={`rm-${field.name}`} className="rm-pw-input" type={visible[field.name] ? "text" : "password"} name={field.name} value={form[field.name]} onChange={update} placeholder={field.hint} autoComplete={field.autoComplete} aria-invalid={Boolean(errorText(field.name))} required />
                      <button type="button" className="rm-pw-eye" onClick={() => toggle(field.name)} aria-label={`${visible[field.name] ? "Hide" : "Show"} ${field.label.toLowerCase()}`} title={visible[field.name] ? "Hide password" : "Show password"}>
                        {visible[field.name] ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18"/><path d="M10.6 5.2A10.7 10.7 0 0 1 12 5c5.3 0 9 7 9 7a15 15 0 0 1-3.1 3.9"/><path d="M6.4 6.5C3.9 8.3 3 12 3 12s3.7 7 9 7c1.7 0 3.3-.7 4.6-1.7"/><path d="M10 10a3 3 0 0 0 4 4"/></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.7-7 10-7 10 7 10 7-3.7 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                    {errorText(field.name) && <p className="rm-pw-error" role="alert">{errorText(field.name)}</p>}
                  </div>
                ))}
                {errorText("general") && <p className="rm-pw-error" role="alert">{errorText("general")}</p>}
                <div className="rm-pw-actions"><button className="rm-pw-submit" type="submit" disabled={submitting}>{submitting ? "Saving Changes..." : "Save New Password →"}</button></div>
              </form>
            </section>
            <aside className="rm-pw-side">
              <section className="rm-pw-white">
                <div className="rm-pw-white-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/></svg></div>
                <h3>Password Safety Tips</h3>
                <p>A strong, unique password helps reduce the risk of unauthorized access.</p>
                <ul><li><span className="rm-pw-check">✓</span> Use a long password with a mix of characters.</li><li><span className="rm-pw-check">✓</span> Avoid reusing passwords from other sites.</li><li><span className="rm-pw-check">✓</span> Keep your password private.</li></ul>
              </section>
              <section className="rm-pw-tip"><h3>Security Reminder</h3><p>RM CROWN MARKET support should never ask you to share your account password.</p></section>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}
